import React from 'react';
import { Lock, Shield, Database, Eye, Globe, Bell, Mail } from 'lucide-react';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Header */}
          <div className="border-b border-gray-200 bg-gray-50 p-6">
            <div className="flex items-center space-x-3">
              <Lock className="h-8 w-8 text-indigo-600" />
              <h1 className="text-2xl font-bold text-gray-900">Privacy Policy</h1>
            </div>
            <p className="mt-2 text-gray-600">Last updated: {new Date().toLocaleDateString()}</p>
          </div>

          {/* Key Points Summary */}
          <div className="bg-indigo-50 p-6 border-b border-indigo-100">
            <h2 className="text-lg font-semibold text-indigo-900 mb-4">Key Privacy Points</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start space-x-3">
                <Database className="h-5 w-5 text-indigo-600 mt-0.5" />
                <div>
                  <p className="font-medium text-indigo-900">Data Collection</p>
                  <p className="text-sm text-indigo-700">We collect only essential information needed to operate our services</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Shield className="h-5 w-5 text-indigo-600 mt-0.5" />
                <div>
                  <p className="font-medium text-indigo-900">Data Protection</p>
                  <p className="text-sm text-indigo-700">Your data is encrypted and securely stored</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Eye className="h-5 w-5 text-indigo-600 mt-0.5" />
                <div>
                  <p className="font-medium text-indigo-900">Data Usage</p>
                  <p className="text-sm text-indigo-700">Your information is only used to provide and improve our services</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Globe className="h-5 w-5 text-indigo-600 mt-0.5" />
                <div>
                  <p className="font-medium text-indigo-900">Data Sharing</p>
                  <p className="text-sm text-indigo-700">We never sell your personal information to third parties</p>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="p-6 prose prose-indigo max-w-none">
            <h2>1. Introduction</h2>
            <p>
              This Privacy Policy explains how SubleaseHero ("we," "us," or "our"), operated by 21st Q LTD, collects, uses, 
              shares, and protects your personal information when you use our website and services. By using SubleaseHero, 
              you agree to the collection and use of information in accordance with this policy.
            </p>

            <h2>2. Information We Collect</h2>
            
            <h3>2.1 Information You Provide</h3>
            <ul>
              <li><strong>Account Information:</strong> Email address, password, full name, phone number</li>
              <li><strong>Profile Information:</strong> Profile photo, occupation, location, languages spoken, about me description</li>
              <li><strong>Verification Documents:</strong> Government-issued ID, proof of address, employment verification documents</li>
              <li><strong>Listing Information:</strong> Property details, photos, pricing, availability, rules, requirements</li>
              <li><strong>Application Information:</strong> Income details, credit score range, employment status, rental history</li>
              <li><strong>Communication Content:</strong> Messages between users through our platform</li>
              <li><strong>Payment Information:</strong> Payment method details (processed by third-party providers)</li>
            </ul>

            <h3>2.2 Information Automatically Collected</h3>
            <ul>
              <li><strong>Device Information:</strong> IP address, browser type, device type, operating system</li>
              <li><strong>Usage Data:</strong> Pages visited, features used, time spent on platform</li>
              <li><strong>Location Data:</strong> Approximate location based on IP address and precise location when using map features (with permission)</li>
              <li><strong>Cookies and Similar Technologies:</strong> Session data, preferences, analytics information</li>
            </ul>

            <h2>3. How We Use Your Information</h2>
            <ul>
              <li><strong>Core Service Functionality:</strong>
                <ul>
                  <li>Creating and managing your account</li>
                  <li>Processing and displaying listings</li>
                  <li>Facilitating communication between users</li>
                  <li>Processing applications and payments</li>
                  <li>Verifying user identity and information</li>
                </ul>
              </li>
              <li><strong>Security and Fraud Prevention:</strong>
                <ul>
                  <li>Verifying user identity</li>
                  <li>Detecting and preventing fraudulent activity</li>
                  <li>Protecting user accounts and information</li>
                </ul>
              </li>
              <li><strong>Communication:</strong>
                <ul>
                  <li>Sending service-related notifications</li>
                  <li>Providing customer support</li>
                  <li>Sending marketing communications (with consent)</li>
                </ul>
              </li>
              <li><strong>Platform Improvement:</strong>
                <ul>
                  <li>Analyzing usage patterns</li>
                  <li>Improving user experience</li>
                  <li>Developing new features</li>
                </ul>
              </li>
            </ul>

            <h2>4. Information Sharing</h2>
            
            <h3>4.1 With Other Users</h3>
            <ul>
              <li><strong>Public Profile Information:</strong> Basic profile details visible to all users</li>
              <li><strong>Listing Information:</strong> Property details, photos, and requirements</li>
              <li><strong>Contact Information:</strong> Shared only after lease approval and deposit payment</li>
            </ul>

            <h3>4.2 With Service Providers</h3>
            <ul>
              <li><strong>Payment Processors:</strong> For processing payments and deposits</li>
              <li><strong>Identity Verification Services:</strong> For user verification</li>
              <li><strong>Cloud Storage Providers:</strong> For storing user data and content</li>
              <li><strong>Analytics Providers:</strong> For platform usage analysis</li>
            </ul>

            <h3>4.3 Legal Requirements</h3>
            <p>
              We may disclose your information if required by law, regulation, legal process, or governmental request.
            </p>

            <h2>5. Data Security</h2>
            <ul>
              <li>All data is encrypted in transit and at rest</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Strict access controls and authentication requirements</li>
              <li>Automated threat detection and prevention systems</li>
            </ul>

            <h2>6. Data Retention</h2>
            <ul>
              <li>Account information retained while account is active</li>
              <li>Listing data retained for legal and business purposes</li>
              <li>Message history retained for user reference and support</li>
              <li>Verification documents retained as required by law</li>
            </ul>

            <h2>7. Your Rights</h2>
            <ul>
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Update or correct your information</li>
              <li><strong>Deletion:</strong> Request deletion of your account and data</li>
              <li><strong>Portability:</strong> Receive your data in a structured format</li>
              <li><strong>Objection:</strong> Object to certain data processing</li>
            </ul>

            <h2>8. Children's Privacy</h2>
            <p>
              Our services are not intended for users under 18 years of age. We do not knowingly collect 
              or maintain information from children under 18.
            </p>

            <h2>9. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your own. 
              We ensure appropriate safeguards are in place for such transfers.
            </p>

            <h2>10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by 
              posting the new Privacy Policy on this page and updating the "Last updated" date.
            </p>

            <h2>11. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <ul>
              <li>Email: privacy@subleasehero.com</li>
              <li>Address: 6890 O'Bannon Drive, Las Vegas, Nevada 89117</li>
            </ul>

            <div className="mt-8 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold text-gray-900">California Privacy Rights</h3>
              <p className="text-sm text-gray-600">
                California residents have additional rights under the California Consumer Privacy Act (CCPA). 
                These include the right to know what personal information is collected, used, shared, or sold, 
                and the right to delete personal information.
              </p>
            </div>

            <div className="mt-8 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold text-gray-900">European Privacy Rights</h3>
              <p className="text-sm text-gray-600">
                Users in the European Economic Area (EEA) have specific rights under the General Data Protection 
                Regulation (GDPR). These include the right to access, rectification, erasure, and data portability.
              </p>
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-gray-50 p-6 border-t border-gray-200">
            <div className="flex items-center space-x-3 mb-4">
              <Mail className="h-5 w-5 text-gray-400" />
              <h2 className="text-lg font-semibold text-gray-900">Questions about privacy?</h2>
            </div>
            <p className="text-gray-600 mb-4">
              We take your privacy seriously. If you have any questions or concerns about this Privacy Policy 
              or our data practices, please don't hesitate to contact our Privacy Team.
            </p>
            <a
              href="mailto:privacy@subleasehero.com"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Contact Privacy Team
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;